import React, { useEffect, useRef, useState } from "react";
import {
  LuCalendarSearch,
  LuTicket,
  LuPartyPopper,
  LuMessageCircle,
} from "react-icons/lu";

const steps = [
  {
    number: "01",
    title: "find a gathering",
    desktopTitle: "Find a gathering",
    text: "Browse upcoming gatherings in your city, each planned around a fun experience.",
    Icon: LuCalendarSearch,
  },
  {
    number: "02",
    title: "reserve your spot",
    desktopTitle: "Reserve your spot",
    text: "Choose the plan you want to join and save your place before it fills up.",
    Icon: LuTicket,
  },
{
  number: "03",
  title: "show up and participate",
  desktopTitle: "Show up and participate",
text: "A trained host welcomes the group, kicks things off, and helps guide the experience.",
  Icon: LuPartyPopper,
},

  {
    number: "04",
    title: "leave with friends",
    desktopTitle: "Leave with friends",
text: "After the gathering, use Tribe to keep chatting with the friends you made and make plans again.", 
Icon: LuMessageCircle,
  },
];


export default function HowItWorksSection() {
  const sectionRef = useRef(null);
  const stepRefs = useRef([]);
  const [visibleSteps, setVisibleSteps] = useState(() =>
    steps.map(() => false)
  );

  useEffect(() => {
    const nodes = stepRefs.current.filter(Boolean);
    if (!nodes.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSteps((current) => {
          const next = [...current];
          let changed = false;

          entries.forEach((entry) => {
            const index = Number(entry.target.dataset.stepIndex);
            const isVisible = entry.isIntersecting;

            if (next[index] !== isVisible) {
              next[index] = isVisible;
              changed = true;
            }
          });

          return changed ? next : current;
        });
      },
      {
        threshold: 0.34,
        rootMargin: "-6% 0px -12% 0px",
      }
    );

    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="howSection"
      style={styles.howSection}
    >
      <style>
        {`
          .desktopHowTitle,
          .desktopHowSub,
          .desktopStepTitle {
            display: inline;
          }

          .mobileHowTitle,
          .mobileHowSub,
          .mobileStepTitle {
            display: none;
          }

          @media (max-width: 450px) {
            .howSection {
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
              display: block !important;
              grid-template-columns: none !important;
              gap: 0 !important;
              border-top: 1px solid rgba(0,0,0,0.08) !important;
              border-bottom: 1px solid rgba(0,0,0,0.08) !important;
              overflow: hidden !important;
            }

            .howInner {
              display: block !important;
              max-width: 1080px !important;
              margin: 0 auto !important;
              padding: 68px 22px 76px !important;
            }

            .howHeader {
              max-width: 390px !important;
              margin-bottom: 36px !important;
            }

            .desktopHowTitle,
            .desktopHowSub,
            .desktopStepTitle {
              display: inline !important;
            }

            .mobileHowTitle,
            .mobileHowSub,
            .mobileStepTitle {
              display: none !important;
            }

            .howTitle {
              margin: 10px 0 0 !important;
              font-size: clamp(2.05rem, 8.6vw, 2.55rem) !important;
              line-height: 1.08 !important;
              letter-spacing: -0.015em !important;
              font-weight: 400 !important;
              text-transform: none !important;
              color: #171717 !important;
            }

            .howSub {
              max-width: 340px !important;
              margin: 14px 0 0 !important;
              font-size: 0.94rem !important;
              line-height: 1.52 !important;
              color: #555 !important;
              font-weight: 430 !important;
              letter-spacing: -0.012em !important;
            }

            .stepsGrid {
              display: block !important;
              border-top: 0px solid rgba(17,17,17,0.08) !important;
            }

            .stepItem {
              display: grid !important;
              grid-template-columns: 64px minmax(0, 1fr) !important;
              gap: 0 !important;
              padding: 22px 0 !important;
              border-bottom: 1px solid rgba(17,17,17,0.08) !important;
              align-items: start !important;
              min-width: 0 !important;
              will-change: opacity, transform !important;
            }

            .stepItem.lastStepItem {
              border-bottom: none !important;
            }

            .stepTop {
              display: flex !important;
              align-items: center !important;
              gap: 9px !important;
              margin: 0 !important;
              color: rgba(17,17,17,0.44) !important;
            }

            .stepNumber {
              font-size: 0.68rem !important;
              line-height: 1 !important;
              font-weight: 600 !important;
              letter-spacing: 0.05em !important;
              color: rgba(23,23,23,0.34) !important;
            }

            .stepIcon {
              font-size: 22px !important;
              width: 22px !important;
              height: 22px !important;
              color: rgba(17,17,17,0.72) !important;
              flex-shrink: 0 !important;
              stroke-width: 1.8 !important;
            }

            .stepLine {
              display: none !important;
            }

            .stepCopy {
              max-width: none !important;
            }

            .stepTitle {
              margin: 0 !important;
              font-size: 1.03rem !important;
              line-height: 1.18 !important;
              letter-spacing: -0.022em !important;
              font-weight: 620 !important;
              color: #171717 !important;
              text-transform: none !important;
            }

            .stepText {
              max-width: 100% !important;
              margin: 7px 0 0 !important;
              font-size: 0.9rem !important;
              line-height: 1.5 !important;
              letter-spacing: -0.01em !important;
              font-weight: 430 !important;
              color: #555 !important;
            }
          }
        `}
      </style>

      <div className="howInner" style={styles.howInner}>
        <div className="howHeader" style={styles.howHeader}>
          <p style={styles.sectionEyebrow}>how it works</p>

          <h2 className="howTitle" style={styles.howTitle}>
            <span className="mobileHowTitle">
              the easiest way to
              <br />
              make friends.
            </span>
            <span className="desktopHowTitle">
               the easiest way to
              <br />
              make friends.
            </span>
          </h2>

          <p className="howSub" style={styles.howSub}>
            <span className="mobileHowSub">
             Pick a plan, show up, and meet new people.
            </span>
            <span className="desktopHowSub">
              Pick a plan, show up, and meet new people.
            </span>
          </p>
        </div>

        <div className="stepsGrid" style={styles.stepsGrid}>
          {steps.map((step, index) => {
            const Icon = step.Icon;
            return (
              <div
                key={step.number}
                ref={(node) => {
                  stepRefs.current[index] = node;
                }}
                data-step-index={index}
                className={`stepItem ${
                  index === steps.length - 1 ? "lastStepItem" : ""
                }`}
                style={{
                  ...styles.stepItem,
                  borderBottom:
                    index === steps.length - 1
                      ? "none"
                      : styles.stepItem.borderBottom,
                  opacity: visibleSteps[index] ? 1 : 0,
                  transform: visibleSteps[index]
                    ? "translateY(0)"
                    : "translateY(12px)",
                  transition: `
                    opacity 420ms ease,
                    transform 520ms cubic-bezier(0.16, 1, 0.3, 1)
                  `,
                }}
              >
                <div className="stepTop" style={styles.stepTop}>
                  <span
                    className="stepNumber"
                    style={{
                      ...styles.stepNumber,
                      opacity: visibleSteps[index] ? 1 : 0,
                      transition: "opacity 320ms ease",
                    }}
                  >
                    {step.number}
                  </span>

                  <Icon
                    className="stepIcon"
                    style={{
                      ...styles.stepIcon,
                      opacity: visibleSteps[index] ? 1 : 0,
                      transform: visibleSteps[index] ? "scale(1)" : "scale(0.92)",
                      transition: `
                        opacity 320ms ease,
                        transform 420ms cubic-bezier(0.16, 1, 0.3, 1)
                      `,
                    }}
                  />

                  <span
                    className="stepLine"
                    style={{
                      ...styles.stepLine,
                      transform: visibleSteps[index] ? "scaleX(1)" : "scaleX(0)",
                      transition:
                        "transform 460ms cubic-bezier(0.16, 1, 0.3, 1)",
                    }}
                  />
                </div>

                <div className="stepCopy" style={styles.stepCopy}>
                  <h3 className="stepTitle" style={styles.stepTitle}>
                    <span className="mobileStepTitle">{step.title}</span>
                    <span className="desktopStepTitle">
                      {step.desktopTitle}
                    </span>
                  </h3>

                  <p className="stepText" style={styles.stepText}>
                    {step.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const styles = {
  howSection: {
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: 1180,
    margin: "0 auto",
    padding: "84px 28px 90px",
    background: "#fff",
    color: "#171717",
    borderTop: "1px solid rgba(17,17,17,0.07)",
    borderBottom: "none",
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "0.82fr 1.18fr",
    gap: 58,
    overflow: "visible",
  },

  howInner: {
    display: "contents",
  },

  howHeader: {
    maxWidth: 430,
    marginBottom: 0,
  },

  sectionEyebrow: {
    margin: 0,
    fontFamily: '"Ugly Handwriting", cursive',
    fontSize: "1.25rem",
    fontWeight: 500,
    lineHeight: 1.15,
    letterSpacing: "normal",
    textTransform: "lowercase",
    color: "#343c54",
  },

  howTitle: {
    fontFamily: '"Instrument Serif", Georgia, serif',
    fontStyle: "normal",
    margin: "12px 0 0",
    color: "#171717",
    fontSize: "clamp(2rem, 3.7vw, 2.8rem)",
    lineHeight: 1.08,
    fontWeight: 400,
    letterSpacing: "-0.015em",
    textTransform: "none",
  },

  howSub: {
    margin: "16px 0 0",
    maxWidth: 390,
    color: "#555",
    fontSize: "0.98rem",
    lineHeight: 1.52,
    fontWeight: 430,
    letterSpacing: "-0.012em",
  },

  stepsGrid: {
    display: "block",
    borderTop: "1px solid rgba(17,17,17,0.08)",
  },

  stepItem: {
    display: "grid",
    gridTemplateColumns: "80px minmax(0, 1fr)",
    gap: 0,
    padding: "21px 0",
    borderBottom: "1px solid rgba(17,17,17,0.08)",
    alignItems: "start",
    minWidth: 0,
    willChange: "opacity, transform",
  },

  stepTop: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 0,
    color: "rgba(17,17,17,0.48)",
  },

  stepNumber: {
    color: "rgba(17,17,17,0.34)",
    fontSize: 11,
    lineHeight: 1,
    fontWeight: 600,
    letterSpacing: "0.05em",
  },

  stepIcon: {
    width: 24,
    height: 24,
    color: "rgba(17,17,17,0.72)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    strokeWidth: 1.8,
    willChange: "opacity, transform",
  },

  stepLine: {
    display: "none",
  },

  stepCopy: {
    maxWidth: 620,
  },

  stepTitle: {
    margin: 0,
    color: "#171717",
    fontSize: "1.03rem",
    lineHeight: 1.18,
    fontWeight: 620,
    letterSpacing: "-0.022em",
    textTransform: "none",
  },

  stepText: {
    margin: "7px 0 0",
    color: "#555",
    maxWidth: 560,
    fontSize: "0.92rem",
    lineHeight: 1.5,
    fontWeight: 430,
    letterSpacing: "-0.01em",
  },
};